import { useEffect, useRef } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from './Button';

/**
 * אישור לפעולה שאי אפשר לבטל אחריה.
 *
 * לא לכל מחיקה. מחיקה של שורה בודדת מקבלת חלון חרטה (ראו Undo.tsx),
 * כי שאלה שנשאלת בכל פעם נלחצת בלי קריאה. כאן נשארות רק שתי
 * משפחות: פעולה שמוחקת גם נתונים שאנשים אחרים יצרו (תשובות לסקר,
 * חתימות על נוהל, התקדמות בלומדה), ופעולה שיוצאת החוצה לאנשים
 * ואי אפשר להחזיר אותה.
 *
 * הכפתור אומר מה יקרה, לא "אישור". הכותרת אומרת על מה מדובר,
 * והגוף אומר מה בדיוק יאבד.
 */
export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  tone = 'bad',
  isPending = false,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: React.ReactNode;
  /** מה הכפתור עושה, בלשון הפעולה: "מחיקת הסקר והתשובות" */
  confirmLabel: string;
  /** bad = הרסני. warn = יוצא החוצה ואי אפשר להחזיר. */
  tone?: 'bad' | 'warn';
  isPending?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  // המיקוד נוחת על "ביטול" ולא על הפעולה ההרסנית: Enter מיד אחרי
  // פתיחה לא אמור למחוק כלום
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div className="fixed inset-0 z-40 grid items-end bg-charcoal/50 p-0 sm:place-items-center sm:p-4">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        className="grid max-h-[85dvh] w-full gap-3 overflow-y-auto rounded-t-lg bg-surface p-4 sm:max-w-md sm:rounded-lg"
      >
        <div className="flex items-start justify-between gap-2">
          <h2 className="m-0 flex items-center gap-2 font-display text-display-m font-semibold">
            <AlertTriangle
              size={22}
              strokeWidth={1.75}
              aria-hidden="true"
              className={tone === 'bad' ? 'text-bad' : 'text-warn'}
            />
            {title}
          </h2>
          <button
            type="button"
            aria-label="סגירה"
            onClick={onCancel}
            className="grid size-11 shrink-0 cursor-pointer place-items-center rounded-sm bg-transparent text-ink hover:bg-cream"
          >
            <X size={20} strokeWidth={1.75} aria-hidden="true" />
          </button>
        </div>

        <div className="grid gap-2 text-body text-ink">{body}</div>

        <div className="flex flex-wrap gap-2">
          <Button variant="primary" disabled={isPending} onClick={onConfirm}>
            {confirmLabel}
          </Button>
          <Button ref={cancelRef} variant="secondary" onClick={onCancel}>
            ביטול
          </Button>
        </div>
      </div>
    </div>
  );
}
